// apps/web/app/(tribute)/tribute/TributeListClient.tsx
"use client";

import type { Tables } from "@/types/supabase";
import { format } from "date-fns";
import Link from "next/link";
import { useMemo, useState } from "react";

type Tribute = Tables<"tributes">;

interface TributeListClientProps {
  tributes: Tribute[];
}

type SortOption = "newest" | "oldest" | "alpha";

export function TributeListClient({ tributes }: TributeListClientProps) {
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<SortOption>("newest");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();

    let result = [...tributes];

    if (q.length > 0) {
      result = result.filter((tribute) => {
        const haystack = [
          tribute.title ?? "",
          tribute.description ?? "",
          tribute.story ?? "",
          tribute.funeral_location ?? "",
        ]
          .join(" ")
          .toLowerCase();

        return haystack.includes(q);
      });
    }

    result.sort((a, b) => {
      if (sort === "alpha") {
        const at = (a.title ?? "").toLowerCase();
        const bt = (b.title ?? "").toLowerCase();
        return at.localeCompare(bt);
      }

      const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
      const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;

      if (sort === "newest") {
        return bTime - aTime;
      }

      // oldest
      return aTime - bTime;
    });

    return result;
  }, [tributes, query, sort]);

  const totalCount = tributes.length;
  const visibleCount = filtered.length;

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex-1">
          <label className="block text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">
            Search tributes
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search by name, description, or location"
              className="mt-1 w-full rounded-2xl border border-border bg-card px-3 py-2 text-sm text-foreground outline-none ring-0 transition focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
          </label>
        </div>

        <div className="flex flex-none items-end gap-3">
          <div className="text-[0.7rem] text-muted-foreground">
            <span className="font-medium text-foreground">{visibleCount}</span>{" "}
            of <span className="font-medium text-foreground">{totalCount}</span>{" "}
            tributes
          </div>

          <label className="text-xs text-muted-foreground">
            <span className="mb-1 block text-[0.7rem] font-medium uppercase tracking-[0.16em]">
              Sort
            </span>
            <select
              value={sort}
              onChange={(event) => setSort(event.target.value as SortOption)}
              className="w-32 rounded-2xl border border-border bg-card px-3 py-2 text-xs text-foreground outline-none ring-0 transition focus:border-primary focus:ring-2 focus:ring-primary/20"
            >
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
              <option value="alpha">A–Z</option>
            </select>
          </label>
        </div>
      </div>

      {visibleCount === 0 ? (
        <EmptyState />
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((tribute) => (
            <li key={tribute.id}>
              <TributeCard tribute={tribute} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function TributeCard({ tribute }: { tribute: Tribute }) {
  return (
    <Link
      href={`/tribute/${tribute.id}`}
      className="group flex h-full flex-col overflow-hidden rounded-3xl border border-border bg-card shadow-sm transition hover:border-primary/50 hover:shadow-md"
    >
      {/* Image */}
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
        {tribute.photo_url ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={tribute.photo_url}
            alt={tribute.title ?? "Tribute photo"}
            className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">
            No photo yet
          </div>
        )}

        {tribute.created_at && (
          <div className="pointer-events-none absolute left-3 top-3 rounded-full bg-background/80 px-2 py-0.5 text-[0.65rem] font-medium uppercase tracking-[0.16em] text-muted-foreground shadow-sm backdrop-blur">
            {safeFormat(tribute.created_at, "MMM d, yyyy")}
          </div>
        )}
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col gap-2 p-4">
        <div className="space-y-1">
          <h2 className="line-clamp-1 text-sm font-semibold tracking-tight text-foreground">
            {tribute.title ?? "Untitled tribute"}
          </h2>
          <p className="text-[0.75rem] text-muted-foreground">
            {renderDates(tribute)}
          </p>
        </div>

        {tribute.description && (
          <p className="line-clamp-3 text-xs leading-relaxed text-muted-foreground">
            {tribute.description}
          </p>
        )}

        {tribute.funeral_location && (
          <p className="mt-auto text-[0.7rem] text-muted-foreground">
            <span className="font-medium text-foreground">Service:</span>{" "}
            {tribute.funeral_location}
          </p>
        )}
      </div>
    </Link>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-border bg-muted/40 px-6 py-10 text-center text-sm text-muted-foreground sm:px-10">
      <p className="mb-1 font-medium text-foreground">
        No tributes match your search.
      </p>
      <p className="mb-4 text-xs">
        Try a different name, or create a new tribute for someone you&apos;re
        honoring.
      </p>
      <Link href="/tribute/create" className="btn btn-outline btn-sm">
        Create a tribute
      </Link>
    </div>
  );
}

function safeFormat(value: string | null, pattern: string): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  try {
    return format(date, pattern);
  } catch {
    return "";
  }
}

function renderDates(tribute: Tribute): string {
  const { birthdate, deathdate } = tribute;

  if (!birthdate && !deathdate) {
    return "";
  }

  const birth = safeFormat(birthdate ?? null, "PPP") || "—";
  const death = safeFormat(deathdate ?? null, "PPP") || "—";

  return `${birth} \u2014 ${death}`;
}
